/**
 * "Let's find out where to start!"
 *
 * A ten-year-old with two years of school English should not spend three weeks
 * on *hello* ([Adaptive engine](../../../docs/ADAPTIVE-ENGINE.md#placement-test)).
 * Offered once, at first launch, and re-runnable from the profile.
 *
 * **It is never a test.** Sneakers runs it as a game, there is no score, no
 * level, no grade, and no feedback on individual answers — being told *"wrong"*
 * eleven times before you have started is how a child decides they are bad at
 * English. The bar shows how far through they are and nothing else.
 *
 * **Skipping is a real answer**, offered on the first screen and on every
 * question, and it starts them at the beginning, which is always legitimate.
 *
 * The questions are ordinary exercises rendered by the ordinary widgets. The
 * search that chooses them is the server's; this screen asks, reports what
 * happened, and asks again.
 */

import { useCallback, useEffect, useState } from 'react';
import { type Client, type Trouble, troubleFrom } from './api.js';
import { ExerciseView, type Verdict } from './exercises.js';
import Sneakers from './Sneakers.js';
import { learningNow } from './language.js';

/** One answer, as the server needs it back. */
interface Answered {
  readonly chapterId: string;
  readonly correct: boolean;
}

type Step =
  | {
      readonly kind: 'ask';
      readonly chapterId: string;
      readonly asked: number;
      readonly total: number;
      readonly item: { readonly exerciseId: string; readonly payload?: unknown };
    }
  | { readonly kind: 'settled'; readonly chapterId: string; readonly titleL1: string };

/** Read the server's answer defensively: it may be older than this app (D-066). */
function readStep(value: unknown): Step | null {
  if (typeof value !== 'object' || value === null) return null;
  const step = value as Partial<Step> & { item?: { exerciseId?: unknown } };
  if (step.kind === 'settled' && typeof step.chapterId === 'string') {
    return {
      kind: 'settled',
      chapterId: step.chapterId,
      titleL1: typeof step.titleL1 === 'string' ? step.titleL1 : 'the beginning',
    };
  }
  if (
    step.kind === 'ask' &&
    typeof step.chapterId === 'string' &&
    typeof step.item?.exerciseId === 'string'
  ) {
    return {
      kind: 'ask',
      chapterId: step.chapterId,
      asked: typeof step.asked === 'number' ? step.asked : 0,
      total: typeof step.total === 'number' && step.total > 0 ? step.total : 12,
      item: step.item,
    };
  }
  return null;
}

/**
 * A chapter title with one full stop, not two.
 *
 * Chapter 2 is called *What's Your Name?*, and appending a period to it read as
 * `What's Your Name?.` — which is the sort of thing that is invisible in a test
 * and obvious in a screenshot.
 */
const sentence = (title: string): string =>
  /[.!?]$/.test(title.trim()) ? title.trim() : `${title.trim()}.`;

export default function Placement({
  client,
  onDone,
}: {
  readonly client: Client;
  /** Called with the chapter to open on, once it is written down. */
  readonly onDone: (chapterId: string) => void;
}) {
  const [step, setStep] = useState<Step | null>(null);
  const [answers, setAnswers] = useState<readonly Answered[]>([]);
  const [started, setStarted] = useState(false);
  const [trouble, setTrouble] = useState<Trouble | null>(null);
  const [saving, setSaving] = useState(false);

  const advance = useCallback(
    async (next: readonly Answered[]) => {
      try {
        const answer: unknown = await Promise.resolve().then(() =>
          client.placement.step.mutate({ answers: [...next] }),
        );
        const read = readStep(answer);
        if (read === null) throw new Error('unreadable step');
        setStep(read);
      } catch (error) {
        setTrouble(troubleFrom(error));
      }
    },
    [client],
  );

  useEffect(() => {
    if (!started) return;
    void advance(answers);
    // Only when the answers change: `advance` is stable and `started` gates it.
  }, [started, answers, advance]);

  /**
   * Write it down and leave.
   *
   * Also how skipping is recorded — there is no separate route, because the
   * outcome of skipping *is* a placement, at the first chapter, and writing it
   * is what stops the app offering the test again tomorrow.
   */
  const settle = useCallback(
    async (chapterId: string) => {
      setSaving(true);
      try {
        await Promise.resolve().then(() =>
          client.placement.accept.mutate({ chapterId }),
        );
        onDone(chapterId);
      } catch (error) {
        setSaving(false);
        setTrouble(troubleFrom(error));
      }
    },
    [client, onDone],
  );

  const skip = useCallback(async () => {
    try {
      const answer: unknown = await Promise.resolve().then(() =>
        client.placement.first.query(),
      );
      const chapterId = (answer as { chapterId?: unknown } | null)?.chapterId;
      // Nothing to place them at is not an error a child should see: the app
      // opens where it would have anyway.
      if (typeof chapterId !== 'string') onDone('');
      else await settle(chapterId);
    } catch (error) {
      setTrouble(troubleFrom(error));
    }
  }, [client, onDone, settle]);

  const onAnswer = useCallback((chapterId: string, verdict: Verdict) => {
    // `seen` means the widget showed something it does not assess. It is not
    // evidence either way, so it must not move the search — asking again is
    // the only honest response.
    if (verdict.kind !== 'answered') return;
    setStep(null);
    setAnswers((sofar) => [
      ...sofar,
      { chapterId, correct: verdict.outcome === 'correct' },
    ]);
  }, []);

  if (trouble !== null) {
    return (
      <div className="grow pad center">
        <p className="sub" role="alert">
          {trouble.kind === 'offline'
            ? 'No connection. Is the wi-fi on?'
            : 'That did not work.'}
        </p>
        <button className="btn" type="button" onClick={() => void skip()}>
          Start at the beginning
        </button>
      </div>
    );
  }

  if (!started) {
    return (
      <div className="grow pad center">
        <Sneakers
          text={`New to ${learningNow().title}? Start at the beginning. Know some already? A few quick questions, and I'll find the right place.`}
          audioRef={null}
          size={120}
        />
        {/*
          The beginning first (D-179). Most children this app is for are
          beginners, and a test taken by a grown-up who knows English, or a
          child who guesses well, used to drop them deep into the course.
        */}
        <button className="btn" type="button" onClick={() => void skip()}>
          Start at the beginning
        </button>
        <button className="btn ghost" type="button" onClick={() => setStarted(true)}>
          Let’s find out
        </button>
      </div>
    );
  }

  if (step === null) {
    return (
      <div className="grow pad center">
        <p className="sub">Thinking…</p>
      </div>
    );
  }

  if (step.kind === 'settled') {
    return (
      <div className="grow pad center">
        {/*
          The whole output of the test, and deliberately all of it. No score, no
          level, no "you are better than 60% of children" — just a place to
          start ([Adaptive engine](../../../docs/ADAPTIVE-ENGINE.md#placement-test)).
        */}
        <Sneakers
          text={`Got it! Let's start here: ${sentence(step.titleL1)}`}
          audioRef={null}
          size={120}
        />
        <button
          className="btn"
          type="button"
          disabled={saving}
          onClick={() => void settle(step.chapterId)}
        >
          Start here
        </button>
      </div>
    );
  }

  const pct = Math.min(100, Math.round((step.asked / step.total) * 100));
  return (
    <div className="grow pad">
      <div
        className="bar"
        role="progressbar"
        aria-label={`Question ${String(step.asked + 1)}`}
        aria-valuenow={step.asked}
        aria-valuemin={0}
        aria-valuemax={step.total}
      >
        <i style={{ width: `${String(pct)}%` }} />
      </div>
      <ExerciseView
        exercise={step.item}
        onDone={(verdict) => onAnswer(step.chapterId, verdict)}
      />
      <button className="btn ghost" type="button" onClick={() => void skip()}>
        Start at the beginning instead
      </button>
    </div>
  );
}
